// שדה הכתיבה של השיחה (11 §1) כרכיב (web-ai-stage-layout): ה-state (טקסט, קבצים, שליחה) ב-Ai.tsx; הרכיב מצויר במקום אחד
// בכל רגע — בתחתית התמליל (sticky), או בתחתית ההגדלה כשבלוק מוגדל (אפשר להמשיך לדבר מול הבלוק בגדול).
// תקציב גובה (web-screen-budget): שורה אחת שגדלה עם התוכן עד 8 שורות (ואז גלילה פנימית), צירוף/עצירה/שליחה באותה שורה;
// מצב החיבור — שורה קטנה מתחת רק כשאינו "מחובר". מיקרופון (conversation-voice-input) בין השדה לשליחה — רק כשהתמלול מוגדר. אחרי שליחה הטקסט מתרוקן והשדה חוזר לשורה אחת.
import { useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react';
import { UPLOAD_ACCEPT } from '../../lib/aiContract.ts';
import type { SseState } from '../../lib/sse.ts';
import { T } from '../../lib/strings.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import { PendingChips, type PendingFile } from './Attachments.tsx';
import { VoiceButton } from './VoiceButton.tsx';

/** מספר השורות המרבי לפני גלילה פנימית. */
export const COMPOSER_MAX_LINES = 8;

/** גובה השדה לפי התוכן: שורה אחת עד COMPOSER_MAX_LINES; מעבר לכך — גלילה פנימית. */
export function fitComposer(el: HTMLTextAreaElement) {
  const cs = getComputedStyle(el);
  const line = parseFloat(cs.lineHeight) || 24;
  const chrome = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  const max = line * COMPOSER_MAX_LINES + chrome;
  el.style.height = 'auto';
  const want = el.scrollHeight + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  el.style.height = `${Math.min(want, max)}px`;
  el.style.overflowY = want > max ? 'auto' : 'hidden';
}

export function Composer({
  level,
  text,
  setText,
  pending,
  onFiles,
  onRemoveFile,
  onSubmit,
  onStop,
  live,
  stopping,
  busy,
  stream,
  showStream,
  inExpand = false,
  centered = false,
  jump,
}: {
  level: 1 | 2;
  text: string;
  setText: (t: string) => void;
  pending: PendingFile[];
  onFiles: (files: Iterable<File>) => void;
  onRemoveFile: (key: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  live: boolean;
  stopping: boolean;
  busy: boolean;
  stream: SseState;
  showStream: boolean;
  /** בתחתית ההגדלה — לא sticky */
  inExpand?: boolean;
  /** שיחה ריקה: השדה במרכז המסך (web-visual-identity) — לא sticky, בלי קו עליון */
  centered?: boolean;
  /** "↓ לסוף" מעל השדה (בתמליל בלבד) */
  jump?: ReactNode;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  // הקלטה קולית (conversation-voice-input): הטקסט מתווסף אחרי מה שכבר כתוב (גם אם נכתב בזמן התמלול) והשדה בפוקוס לעריכה
  const latest = useRef(text);
  latest.current = text;
  const [voiceErr, setVoiceErr] = useState<string | null>(null);
  const onVoiceText = (t: string) => {
    const cur = latest.current;
    setText(cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t);
    requestAnimationFrame(() => {
      const el = area.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  };
  useLayoutEffect(() => {
    if (area.current) fitComposer(area.current);
  }, [text]);
  const placeholder = live ? T.ai.placeholderLive : level === 1 ? T.ai.placeholder1 : T.ai.placeholder2;
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit();
    }
  };
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = [...(e.clipboardData?.files ?? [])];
    if (!files.length) return;
    e.preventDefault();
    onFiles(files);
  };
  return (
    <form
      className={cn('flex flex-col gap-2 bg-background', inExpand ? 'mx-auto w-full max-w-4xl' : centered ? 'w-full max-w-3xl' : 'sticky bottom-(--ab-foot) z-10 mt-auto border-t border-border py-2.5')}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      data-testid="composer"
      data-place={inExpand ? 'expand' : centered ? 'center' : 'page'}
    >
      {jump}
      <label htmlFor="composer" className="sr-only">
        {placeholder}
      </label>
      <PendingChips files={pending} onRemove={onRemoveFile} disabled={busy} />
      <div className="flex items-end gap-2">
        {/* צירוף קובץ: במובייל הבורר המקורי (מצלמה/תמונות/קבצים); multiple; רק הסוגים שה-api מקבל */}
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={UPLOAD_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          data-testid="file-input"
          onChange={(e) => {
            onFiles(e.target.files ?? []);
            e.target.value = '';
          }}
        />
        <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => fileInput.current?.click()} disabled={busy} aria-label={T.ai.attach.button} title={T.ai.attach.hint} data-testid="attach-btn">
          <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5 12.7 19.8a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" />
          </svg>
        </Button>
        {/* תור חי: השדה פתוח ("אפשר לכתוב גם עכשיו…") — נעול רק בזמן עצירה (ממתינים ל-done) */}
        <textarea
          ref={area}
          id="composer"
          data-testid="composer-input"
          rows={1}
          dir="auto"
          value={text}
          disabled={stopping}
          onChange={(e) => {
            setText(e.target.value);
            if (voiceErr) setVoiceErr(null);
          }}
          onKeyDown={onKey}
          onPaste={onPaste}
          placeholder={placeholder}
          className="min-h-11 min-w-0 flex-1 resize-none overflow-y-hidden rounded-sm border border-input bg-card px-3 py-2 text-base leading-6 disabled:opacity-60"
        />
        <VoiceButton disabled={stopping} onText={onVoiceText} onError={setVoiceErr} />
        {live && (
          <Button type="button" variant="outline" className="shrink-0" onClick={onStop} disabled={stopping} data-testid="stop-btn">
            {stopping ? T.ai.stopping : T.ai.stop}
          </Button>
        )}
        <Button type="submit" className="shrink-0" disabled={busy || (!text.trim() && !pending.some((f) => !f.errorHe))} data-testid="send-btn">
          {T.ai.send}
        </Button>
      </div>
      {voiceErr && (
        <span className="text-xs text-error" role="alert" data-testid="voice-error">
          {voiceErr}
        </span>
      )}
      {/* מצב החיבור: שורה רק כשיש מה לומר (לא "מחובר") — ה-data-state תמיד */}
      <span className="text-xs text-muted-foreground empty:hidden" role="status" data-testid="stream-state" data-state={stream}>
        {showStream && stream !== 'open' ? T.ai.stream[stream] : ''}
      </span>
    </form>
  );
}
